import React, { useState } from "react";
import {
  Box,
  Grid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  HStack,
  VStack,
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";

const DividendKPI = ({
  monthlyDividends = 3.865,
  stockDividends = [4.0784, 0.1695, 5.0331, 3.7402, 3.0083, 2.8753], // Annual dividends / stock
  milestonesAchieved = 3,
  totalMilestones = 72,
  totalPaidDividends = 25.04,
  onCardClick,
}) => {
  const [hoveredCard, setHoveredCard] = useState(null);

  // === YOUR ORIGINAL FORMULA — preserved ===
  const avgDivPerStock = (
    monthlyDividends / stockDividends.reduce((a, b) => a + b, 0)
  ).toFixed(2);

  const formatNumber = (v) =>
    Number(v).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // === Cards definition ===
  const cards = [
    {
      id: "monthly",
      label: "Monthly Dividends",
      emoji: "💰",
      value: `$${formatNumber(monthlyDividends)} / month`,
      color: "#48BB78",
      gradient: "linear-gradient(135deg,#48BB78,#68D391)",
      subtitle: "Click to view details",
      route: "/stocks",
    },
    {
      id: "avg",
      label: "Avg Div/Stock",
      emoji: "💵",
      value: `$${avgDivPerStock}`, // your correct number (0.32)
      color: "#4299E1",
      gradient: "linear-gradient(135deg,#4299E1,#63B3ED)",
      subtitle: "Based on all stocks",
      route: "/stocks",
    },
    {
      id: "milestones",
      label: "Milestones",
      emoji: "🎯",
      value: `${milestonesAchieved} / ${totalMilestones}`,
      color: "#38B2AC",
      gradient: "linear-gradient(135deg,#38B2AC,#4FD1C5)",
      subtitle: "Progress overview",
      route: "/milestones",
      progress: (milestonesAchieved / totalMilestones) * 100,
    },
    {
      id: "total",
      label: "Total Dividends Paid",
      emoji: "📦",
      value: `$${formatNumber(totalPaidDividends)}`,
      color: "#ECC94B",
      gradient: "linear-gradient(135deg,#ECC94B,#F6E05E)",
      subtitle: "All time earnings",
      route: "/stocks",
    },
  ];

  // === Generic Card Renderer ===
  const renderCard = (card) => {
    const isHovered = hoveredCard === card.id;

    return (
      <Box
        key={card.id}
        position="relative"
        onMouseEnter={() => setHoveredCard(card.id)}
        onMouseLeave={() => setHoveredCard(null)}
        onClick={() => onCardClick?.(card.route)}
        cursor="pointer"
      >
        {/* Glow */}
        <Box
          position="absolute"
          inset="-3px"
          borderRadius="xl"
          bg={card.gradient}
          opacity={isHovered ? 0.3 : 0}
          filter="blur(18px)"
          transition="0.35s"
        />

        {/* Actual Card */}
        <Box
          bg="linear-gradient(135deg, rgba(26,32,44,0.95), rgba(45,55,72,0.95))"
          p={5}
          borderRadius="xl"
          border="2px solid"
          borderColor={isHovered ? card.color : "rgba(255,221,64,0.25)"}
          transition="0.35s"
          transform={isHovered ? "translateY(-6px) scale(1.02)" : "scale(1)"}
          boxShadow={
            isHovered
              ? `0 20px 40px ${card.color}44`
              : "0 10px 25px rgba(0,0,0,0.35)"
          }
          position="relative"
        >
          {/* Corner decoration */}
          <Box
            position="absolute"
            top={2}
            right={2}
            w="22px"
            h="22px"
            borderTop="2px solid"
            borderRight="2px solid"
            borderColor={card.color}
            borderRadius="0 8px 0 0"
            opacity={isHovered ? 0.6 : 0.3}
            transition="0.35s"
          />

          <Stat>
            {/* Label + Arrow */}
            <HStack justify="space-between">
              <StatLabel
                color="gray.400"
                fontSize="sm"
                fontWeight="bold"
                letterSpacing="wider"
              >
                {card.emoji} {card.label}
              </StatLabel>

              <ArrowForwardIcon
                color={card.color}
                opacity={isHovered ? 1 : 0}
                transform={isHovered ? "translateX(0)" : "translateX(-5px)"}
                transition="0.25s"
              />
            </HStack>

            {/* Value */}
            <StatNumber
              fontSize="2xl"
              fontWeight="black"
              background={card.gradient}
              backgroundClip="text"
              sx={{ WebkitTextFillColor: "transparent" }}
              textShadow={`0 0 20px ${card.color}33`}
              mt={1}
            >
              {card.value}
            </StatNumber>

            {/* Progress bar */}
            {card.progress !== undefined && (
              <Box
                w="full"
                h="5px"
                bg="rgba(255,255,255,0.1)"
                borderRadius="full"
                mt={2}
                overflow="hidden"
              >
                <Box
                  h="full"
                  bg={card.gradient}
                  borderRadius="full"
                  width={`${card.progress}%`}
                  transition="width 0.8s ease"
                  boxShadow={`0 0 12px ${card.color}55`}
                />
              </Box>
            )}

            {/* Subtitle */}
            <HStack fontSize="xs" color="gray.500" mt={2}>
              <Box
                boxSize="4px"
                bg={card.color}
                borderRadius="full"
                animation="pulse 2s ease-in-out infinite"
              />
              <Text>{card.subtitle}</Text>
            </HStack>
          </Stat>
        </Box>
      </Box>
    );
  };

  return (
    <Box maxW="900px" mx="auto" mt={6} px={3}>
      <Grid templateColumns="repeat(2,1fr)" gap={4}>
        {cards.map(renderCard)}
      </Grid>

      {/* Info Bar */}
      <Box
        mt={4}
        p={3}
        bg="rgba(26,32,44,0.6)"
        borderRadius="lg"
        border="1px solid rgba(255,221,64,0.2)"
      >
        <HStack justify="space-around" fontSize="xs" color="gray.400">
          <Text>Dividend Tracking</Text>
          <Text>•</Text>
          <Text>Monthly: ${formatNumber(monthlyDividends)}</Text>
          <Text>•</Text>
          <Text>Annual: ${formatNumber(monthlyDividends * 12)}</Text>
        </HStack>
      </Box>
    </Box>
  );
};

export default DividendKPI;
